import React, {useMemo, useState} from 'react';
import {Block, Button, Card, Checkbox, Dialog, DialogBody, DialogFooter, ModalComponentProps, Space, Text, VF, makeValidate, toaster} from '@mc/ui-v4';
import {Form, FormRenderProps} from 'react-final-form';
import {OnChange} from 'react-final-form-listeners';
import {BatchUpdateParamsVO, DocumentApi, DocumentVO, EnumInterpretace, EnumTypLayerAttr, LayerAttributeVO} from '../../api';
import {downloadTemporaryFilePreview} from '../../api/urls';
import {useAppConfig} from '@mc/react-app-base';
import {ImageDropzone} from '../image/ImageDropzone';
import {DocumentDropzone} from './document/DocumentDropzone';
import {AccessibilityField} from './AccessibilityField';
import {sortCiselniky} from '../../helpers/Formatter';
import {allowSelectCiselnikList, buildCiselnikSelectTree, flattenCiselnikTree, isCiselnikTree, renderCiselnikTreeItem} from '../../helpers/DataMutators';
import {formSchema, unwrapFieldName} from '../../validation/getFieldSchema';

type BulkFormValues = Record<string, any>;

type Props = ModalComponentProps & {
    attributes: LayerAttributeVO[];
    featureIds: number[];
    actionId: number | null;
    onSubmit: (params: BatchUpdateParamsVO, actionId: number | null) => Promise<void>;
};

export const BulkEditModal: React.FC<Props> = ({attributes, featureIds, actionId, onClose, onSubmit}) => {
    const [selectedNazvy, setSelectedNazvy] = useState<Set<string>>(new Set());
    const [fileObjects, setFileObjects] = useState<DocumentVO[]>([]);
    const appConfig = useAppConfig();

    const editableAttributes = useMemo(() => attributes.filter(a => !a.nazev?.startsWith('ATTR_')), [attributes]);

    const validate = useMemo(() => makeValidate(formSchema(editableAttributes.filter(a => selectedNazvy.has(a.nazev)))), [editableAttributes, selectedNazvy]);

    const toggleAttribute = (nazev: string) => {
        setSelectedNazvy(prev => {
            const next = new Set(prev);
            if (next.has(nazev)) next.delete(nazev);
            else next.add(nazev);
            return next;
        });
    };

    const renderField = (attribute: LayerAttributeVO, submitting: boolean, disabled: boolean) => {
        const label = attribute.popis ?? attribute.nazev;
        const name = attribute.nazev;
        const key = `${name}-${attribute.id}`;
        const fieldDisabled = disabled || submitting;
        const commonProps = {key, name, label, disabled: fieldDisabled, required: attribute.povinnost};
        switch (attribute.typ) {
            case EnumTypLayerAttr.STRING:
                return <VF.String {...commonProps} />;
            case EnumTypLayerAttr.TEXT:
                // Dlouhý text se podle interpretace edituje buď jako formátovaný text (WYSIWYG), nebo jako prostý text.
                if (attribute.interpretace === EnumInterpretace.FORMATOVANYTEXT) {
                    // VF.Wysiwyg (Tiptap) po prvním vykreslení neumí zareagovat na změnu `disabled` (editor
                    // zůstane needitovatelný, i když se zaškrtnutím atributu povolí) – proto ho při přepnutí
                    // stavu remountneme přes klíč, aby se znovu inicializoval s aktuálním `disabled`.
                    return <VF.Wysiwyg {...commonProps} key={`${key}-${fieldDisabled}`} />;
                }
                return <VF.TextArea {...commonProps} />;
            case EnumTypLayerAttr.INT:
                return <VF.Number {...commonProps} className="col-4 pl-0" />;
            case EnumTypLayerAttr.NUMBER:
                return <VF.Number {...commonProps} decimal className="col-4 pl-0" />;
            case EnumTypLayerAttr.BOOLEAN:
                return <VF.Checkbox {...commonProps} type="checkbox" inlineValidation />;
            case EnumTypLayerAttr.ENUM: {
                const isTree = isCiselnikTree(attribute.ciselniky);
                if (attribute.interpretace === EnumInterpretace.MULTI_CISELNIK) {
                    // VF.MultiSelect neumí stromový režim, u stromového číselníku proto hodnoty vypíšeme
                    // v pořadí stromu (rodič, pak jeho potomci) a hierarchii naznačíme odsazením.
                    return (
                        <VF.MultiSelect
                            {...commonProps}
                            items={
                                isTree
                                    ? flattenCiselnikTree(attribute.ciselniky).map(c => ({id: c.id, name: c.nazev, depth: c.depth}))
                                    : (sortCiselniky(attribute.ciselniky)?.map(c => ({id: c.id, name: c.nazev})) ?? [])
                            }
                            renderItem={isTree ? renderCiselnikTreeItem : undefined}
                        />
                    );
                }
                // Jednohodnotový číselník – stromový jako hierarchický výběr, plochý jako běžný výběr.
                // Ve stromu jde vybrat pouze koncová hodnota, uzly slouží jen k seskupení (MCW-29258).
                if (isTree) {
                    return (
                        <VF.SelectBox
                            {...commonProps}
                            items={buildCiselnikSelectTree(attribute.ciselniky)}
                            tree
                            alwaysExpanded
                            useIdAsValue
                            allowSelectItem={allowSelectCiselnikList(attribute.ciselniky)}
                        />
                    );
                }
                return <VF.SelectBox {...commonProps} items={sortCiselniky(attribute.ciselniky)} useIdAsValue />;
            }
            case EnumTypLayerAttr.DATE_TIME:
                return <VF.DateTime {...commonProps} />;
            case EnumTypLayerAttr.ACCESSIBILITY:
                return <AccessibilityField key={key} name={name} label={label} disabled={disabled || submitting} />;
            default:
                return null;
        }
    };

    const handleSubmit = async (data: BulkFormValues) => {
        const properties: Record<string, object> = {};
        const attributeIds: number[] = [];

        for (const attr of editableAttributes) {
            if (selectedNazvy.has(attr.nazev)) {
                // react-final-form ukládá hodnotu pod nezabalený název (viz unwrapFieldName) i pro atributy
                // s tečkou v názvu, které mají `nazev` obalený do `["…"]"` kvůli <Field name>.
                const fieldName = unwrapFieldName(attr.nazev);
                if (attr.typ === EnumTypLayerAttr.BOOLEAN) {
                    // Checkbox vrací undefined, pokud není zaškrtnutý, ale pro backend potřebujeme true/false
                    data[fieldName] = !!data[fieldName];
                }
                properties[fieldName] = data[fieldName];
                if (attr.id != null) {
                    attributeIds.push(Number(attr.id));
                }
            }
        }

        const tmpImages: string[] = (data.tmpImages ?? []).filter((item: any) => typeof item === 'string');
        const tmpDocs = (data.tmpDocs ?? []).map(({nazev: _nazev, ...rest}: any) => rest);

        const params: BatchUpdateParamsVO = {
            featureIds,
            attributeIds,
            feature: {
                properties,
                tmpImages,
                tmpDocs,
            },
        };

        await onSubmit(params, actionId);
        onClose();
    };

    const initialValues = useMemo<BulkFormValues>(() => ({tmpImages: [], tmpDocs: []}), []);
    // Nastavení defaultní hodnoty atributů s boolean na False
    editableAttributes.forEach((att: LayerAttributeVO) => {
        if (att.nazev.includes('.') && !/^\[".*"\]$/.test(att.nazev)) {
            att.nazev = `["${att.nazev}"]`;
        }
    });

    return (
        <Form<BulkFormValues> initialValues={initialValues} keepDirtyOnReinitialize validate={validate} onSubmit={handleSubmit}>
            {({submitting, handleSubmit: formSubmit, form: {change}, values}: FormRenderProps<BulkFormValues>) => (
                <Dialog size="lg" onClose={onClose} title={`Hromadná editace – ${featureIds.length} záznamů`}>
                    <DialogBody>
                        <Space vertical size={'2'}>
                            <Card mode="edit" title="Atributy">
                                <Block>
                                    <Space vertical>
                                        <Text className="mb-1">
                                            Vyberte atributy, které chcete upravit, a zadejte nové hodnoty. Nevybrané atributy zůstanou beze změny.
                                        </Text>
                                        {editableAttributes.map(attribute => {
                                            const isSelected = selectedNazvy.has(attribute.nazev);
                                            return (
                                                <Space
                                                    key={attribute.nazev}
                                                    className={`d-flex align-items-center gap-2 mb-1 ${attribute.typ === EnumTypLayerAttr.BOOLEAN ? 'py-1' : ''}`}
                                                >
                                                    <Checkbox className="mr-1" checked={isSelected} onChange={() => toggleAttribute(attribute.nazev)} />
                                                    <div className="flex-grow-1">{renderField(attribute, submitting, !isSelected)}</div>
                                                </Space>
                                            );
                                        })}
                                        {editableAttributes.length === 0 && <span>Žádné editovatelné atributy.</span>}
                                    </Space>
                                </Block>
                            </Card>
                            <Card mode="edit" title="Fotografie">
                                <Block bodyClassName="pb-0">
                                    <VF.Base
                                        name="tmpImages"
                                        input={ImageDropzone}
                                        label={false}
                                        getImageUrl={(id: any) => `${appConfig.serverContextPath}${downloadTemporaryFilePreview(id)}`}
                                        onImageUpload={async (imageContent: File) => {
                                            const {data} = await DocumentApi.createTemporaryFile('H1', imageContent)
                                                .then(res => res)
                                                .catch(error => {
                                                    if (error.status === 413) {
                                                        toaster.errorWithTitle('Nahrání fotografie se nezdařilo.', 'Soubor je pravděpodobně příliš velký.');
                                                    } else {
                                                        toaster.error('Nahrání fotografie se nezdařilo.');
                                                    }
                                                    throw error;
                                                });
                                            return data;
                                        }}
                                        multiple
                                    />
                                </Block>
                            </Card>
                            <Card mode="edit" title="Dokumenty">
                                <Block bodyClassName="pb-0">
                                    <VF.Base
                                        name="tmpDocs"
                                        input={DocumentDropzone}
                                        label={false}
                                        getDocumentUrl={(id: any) => `${appConfig.serverContextPath}${downloadTemporaryFilePreview(id)}`}
                                        onDocumentUpload={async (imageContent: File) => {
                                            const {data} = await DocumentApi.createTemporaryFile('H1', imageContent)
                                                .then(res => res)
                                                .catch(error => {
                                                    if (error.status === 413) {
                                                        toaster.errorWithTitle('Nahrání dokumentu se nezdařilo.', 'Soubor je pravděpodobně příliš velký.');
                                                    } else {
                                                        toaster.error('Nahrání dokumentu se nezdařilo.');
                                                    }
                                                    throw error;
                                                });
                                            return data;
                                        }}
                                        fileObjects={fileObjects}
                                        setFileObjects={vals => {
                                            setFileObjects(vals);
                                            change('tmpDocs', vals);
                                        }}
                                        multiple
                                    />
                                </Block>
                                <OnChange name="tmpDocs">
                                    {() => {
                                        change('tmpDocs', fileObjects);
                                    }}
                                </OnChange>
                            </Card>
                        </Space>
                    </DialogBody>
                    <DialogFooter>
                        <Button disabled={submitting} onClick={onClose}>
                            Zrušit
                        </Button>
                        <Button
                            style="primary"
                            disabled={submitting || (selectedNazvy.size === 0 && values?.tmpDocs?.length === 0 && values?.tmpImages?.length === 0)}
                            onClick={formSubmit}
                            type="submit"
                        >
                            Uložit změny
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}
        </Form>
    );
};
